import type { Point, Rect } from './camera';
import type { Box } from './tools/tool';

export type AlignMode = 'left' | 'hcenter' | 'right' | 'top' | 'vcenter' | 'bottom';
export type ArrangeMode = 'grid' | 'row' | 'column' | 'masonry';
export type LayoutItem = { id: string; rect: Rect };

/** Default spacing of "Arrange" (backlog §4). */
export const ARRANGE_GAP = 24;
/** Space between a section's sides and its arranged content (the title sits in the top one at 100 %). */
export const ARRANGE_MARGIN = 40;
/** Smallest side an arranged section shrinks to. */
export const SECTION_MIN = 160;

const w = (r: Rect) => r.maxX - r.minX;
const h = (r: Rect) => r.maxY - r.minY;

function union(items: readonly LayoutItem[]): Rect {
  return {
    minX: Math.min(...items.map((i) => i.rect.minX)),
    minY: Math.min(...items.map((i) => i.rect.minY)),
    maxX: Math.max(...items.map((i) => i.rect.maxX)),
    maxY: Math.max(...items.map((i) => i.rect.maxY)),
  };
}

/** Moves (world deltas) that align every item on the selection's bounds. */
export function alignDeltas(items: readonly LayoutItem[], mode: AlignMode): Map<string, Point> {
  const out = new Map<string, Point>();
  if (items.length === 0) return out;
  const u = union(items);
  for (const { id, rect: r } of items) {
    const x = mode === 'left' ? u.minX - r.minX : mode === 'right' ? u.maxX - r.maxX : mode === 'hcenter' ? (u.minX + u.maxX) / 2 - (r.minX + r.maxX) / 2 : 0;
    const y = mode === 'top' ? u.minY - r.minY : mode === 'bottom' ? u.maxY - r.maxY : mode === 'vcenter' ? (u.minY + u.maxY) / 2 - (r.minY + r.maxY) / 2 : 0;
    out.set(id, { x, y });
  }
  return out;
}

/** Equal gaps along `axis` between at least three items; the first and last stay. Empty for fewer items. */
export function distributeDeltas(items: readonly LayoutItem[], axis: 'x' | 'y'): Map<string, Point> {
  const out = new Map<string, Point>();
  if (items.length < 3) return out;
  const lo = (r: Rect) => (axis === 'x' ? r.minX : r.minY);
  const size = (r: Rect) => (axis === 'x' ? w(r) : h(r));
  const sorted = [...items].sort((a, b) => lo(a.rect) - lo(b.rect) || (a.id < b.id ? -1 : 1));
  const first = sorted[0]!.rect;
  const last = sorted.at(-1)!.rect;
  const span = (axis === 'x' ? last.maxX : last.maxY) - lo(first);
  const gap = (span - sorted.reduce((s, i) => s + size(i.rect), 0)) / (sorted.length - 1);
  let at = lo(first);
  sorted.forEach((i, k) => {
    const d = k === 0 || k === sorted.length - 1 ? 0 : at - lo(i.rect);
    out.set(i.id, axis === 'x' ? { x: d, y: 0 } : { x: 0, y: d });
    at += size(i.rect) + gap;
  });
  return out;
}

/**
 * Top to bottom, then left to right: an item whose top is within half the smaller height of a row's first item
 * belongs to that row.
 */
export function readingOrder(items: readonly LayoutItem[]): string[] {
  const byTop = [...items].sort((a, b) => a.rect.minY - b.rect.minY || a.rect.minX - b.rect.minX || (a.id < b.id ? -1 : 1));
  const rows: LayoutItem[][] = [];
  for (const it of byTop) {
    const row = rows.at(-1);
    const head = row?.[0];
    if (head && it.rect.minY - head.rect.minY < Math.min(h(it.rect), h(head.rect)) / 2) row!.push(it);
    else rows.push([it]);
  }
  return rows.flatMap((row) => row.sort((a, b) => a.rect.minX - b.rect.minX || (a.id < b.id ? -1 : 1)).map((i) => i.id));
}

/**
 * New top-left corners (world) for a tidy layout of the items in reading order, starting at the selection's top-left
 * corner: one row, one column, a grid (rows as high as their tallest item, columns as wide as their widest) or a
 * masonry (equal columns, each item in the shortest one). Items never overlap.
 */
export function arrangeLayout(items: readonly LayoutItem[], mode: ArrangeMode, gap: number): Map<string, Point> {
  const out = new Map<string, Point>();
  if (items.length === 0) return out;
  const g = Math.max(0, gap);
  const byId = new Map(items.map((i) => [i.id, i.rect]));
  // Masonry reads strictly top then left: its own output (columns of uneven tops) then reads back in placement
  // order, so arranging twice changes nothing.
  const ids =
    mode === 'masonry'
      ? [...items].sort((a, b) => a.rect.minY - b.rect.minY || a.rect.minX - b.rect.minX || (a.id < b.id ? -1 : 1)).map((i) => i.id)
      : readingOrder(items);
  const order = ids.map((id) => ({ id, r: byId.get(id)! }));
  const u = union(items);
  const ox = u.minX;
  const oy = u.minY;
  if (mode === 'row' || mode === 'column') {
    let at = mode === 'row' ? ox : oy;
    for (const { id, r } of order) {
      out.set(id, mode === 'row' ? { x: at, y: oy } : { x: ox, y: at });
      at += (mode === 'row' ? w(r) : h(r)) + g;
    }
    return out;
  }
  const cols = Math.ceil(Math.sqrt(order.length));
  if (mode === 'grid') {
    const colW = Array.from({ length: cols }, (_, c) => Math.max(...order.filter((_, i) => i % cols === c).map((o) => w(o.r))));
    const rows = Math.ceil(order.length / cols);
    const rowH = Array.from({ length: rows }, (_, k) => Math.max(...order.slice(k * cols, (k + 1) * cols).map((o) => h(o.r))));
    order.forEach(({ id }, i) => {
      const c = i % cols;
      const k = Math.floor(i / cols);
      const x = ox + colW.slice(0, c).reduce((s, v) => s + v + g, 0);
      const y = oy + rowH.slice(0, k).reduce((s, v) => s + v + g, 0);
      out.set(id, { x, y });
    });
    return out;
  }
  const colW = Math.max(...order.map((o) => w(o.r)));
  const heights = new Array<number>(cols).fill(0);
  for (const { id, r } of order) {
    const c = heights.indexOf(Math.min(...heights));
    out.set(id, { x: ox + c * (colW + g), y: oy + heights[c]! });
    heights[c]! += h(r) + g;
  }
  return out;
}

/**
 * `arrangeLayout` inside a container: the items start `margin` inside `frame`'s top-left corner, and the frame (top-left
 * fixed) is resized around them with the same margin, never under `SECTION_MIN` on a side.
 */
export function arrangeInside(frame: Rect, items: readonly LayoutItem[], mode: ArrangeMode, gap: number, margin: number): { positions: Map<string, Point>; frame: Rect } {
  const laid = arrangeLayout(items, mode, gap);
  const positions = new Map<string, Point>();
  if (!items.length) return { positions, frame };
  const u = union(items);
  const dx = frame.minX + margin - u.minX;
  const dy = frame.minY + margin - u.minY;
  let maxX = frame.minX + SECTION_MIN;
  let maxY = frame.minY + SECTION_MIN;
  for (const it of items) {
    const p = laid.get(it.id)!;
    const at = { x: p.x + dx, y: p.y + dy };
    positions.set(it.id, at);
    maxX = Math.max(maxX, at.x + w(it.rect) + margin);
    maxY = Math.max(maxY, at.y + h(it.rect) + margin);
  }
  return { positions, frame: { minX: frame.minX, minY: frame.minY, maxX, maxY } };
}

/**
 * Where a new neighbor of `src` goes in direction `dir`: same size, `gap` away, pushed further along `dir` (one slot
 * at a time) while it overlaps an occupied rectangle.
 */
export function neighborSlot(src: { x: number; y: number; w: number; h: number }, dir: 'top' | 'right' | 'bottom' | 'left', occupied: readonly Rect[], gap = ARRANGE_GAP) {
  const sx = dir === 'right' ? src.w + gap : dir === 'left' ? -(src.w + gap) : 0;
  const sy = dir === 'bottom' ? src.h + gap : dir === 'top' ? -(src.h + gap) : 0;
  const slot = { x: src.x + sx, y: src.y + sy, w: src.w, h: src.h };
  const hits = (r: Rect) => slot.x < r.maxX && r.minX < slot.x + slot.w && slot.y < r.maxY && r.minY < slot.y + slot.h;
  for (let i = 0; i < 10_000 && occupied.some(hits); i++) {
    slot.x += sx;
    slot.y += sy;
  }
  return slot;
}

/** "Fit to content" (P5e): the box around a section's children, `margin` on every side, never under `min`; null without any. */
export function fitSectionBox(children: readonly Rect[], margin = ARRANGE_MARGIN, min = SECTION_MIN): Box | null {
  if (!children.length) return null;
  const u = union(children.map((rect) => ({ id: '', rect })));
  return { x: u.minX - margin, y: u.minY - margin, w: Math.max(min, w(u) + 2 * margin), h: Math.max(min, h(u) + 2 * margin) };
}
